import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config';

// เงินที่ IMD ส่งมา — Vector 2026-09-27: "ให้บัญชีปลดล็อกจากหน้าเว็บ saleDataDetail ด้วย และสามารถแกไข
// ยอดเงินที่ส่งมาด้วยในหน้านั้น และส่วน IMD ทุกครั้งที่กดส่งยอดเงินให้ทำการ แค็บหน้าจอเก็บรูปไว้ 30 วันด้วย"
// Contract: icecys-repos/MONEY-AUDIT-CONTRACT.md (seller-module web endpoints 1-3).
// Screenshots are NOT shown here (Vector scope change 2026-09-27) even though GET returns them.
// Every write carries a mandatory reason; the server records who (X-USER-UID) + before/after.

const BASE = `${API_BASE_URL}/seller/api/tablet/report/web`;

const authToken = () => JSON.parse(localStorage.getItem('user') || 'null')?.token;

// The seller-module DTOs are CashDenominationEntry {denomination, quantity} and
// ExpenseEntry {category, amount, note}; the contract draft called them count/label. Read either.
const denomCount = (d) => Number(d?.quantity ?? d?.count ?? 0) || 0;
const expenseLabel = (e) => e?.category ?? e?.label ?? '';

const num = (v) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

export const formatIct = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return new Intl.DateTimeFormat('th-TH', {
        timeZone: 'Asia/Bangkok',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
};

// ยอดโอนรายครั้ง (Vector 2026-10-01). Old rows only carry the total, so fall back to [total].
const transfersOf = (data) => {
    if (Array.isArray(data?.bankTransfers)) return data.bankTransfers.map(Number);
    const legacy = Number(data?.bankTransferAmount) || 0;
    return legacy > 0 ? [legacy] : [];
};

const cashTotalOf = (denoms) => denoms.reduce((s, d) => s + (Number(d.denomination) || 0) * denomCount(d), 0);
const expensesTotalOf = (exps) => exps.reduce((s, e) => s + (Number(e.amount) || 0), 0);

const statusOf = (data) => {
    if (!data?.submitted) return { text: 'ยังไม่ส่ง', color: '#6c757d' };
    if (data.source === 'ACCOUNTING') return { text: 'บัญชีกรอกแล้ว', color: '#2e7d32' };
    if (data.locked) return { text: 'ส่งแล้ว (ล็อกอยู่)', color: '#2e7d32' };
    return { text: 'ปลดล็อกแล้ว รอคนขับส่งใหม่', color: '#ef6c00' };
};

const ACTION_LABEL = { UNLOCK: 'ปลดล็อก', EDIT: 'แก้ยอด', CREATE: 'บัญชีกรอกยอดครั้งแรก' };

// ผลต่างระหว่างที่กรอก กับ ที่ต้องส่งตามระบบ — บวก = เกิน (เขียว) · ลบ = ขาด (แดง) · ใกล้ 0 = ตรง (เขียว)
export const diffTone = (diff) => (Math.abs(diff) < 0.5 ? { text: 'ตรง', color: '#2e7d32' } : diff > 0 ? { text: 'เกิน', color: '#2e7d32' } : { text: 'ขาด', color: '#c62828' });

const readError = async (res) => (await res.text().catch(() => '')) || `HTTP ${res.status}`;

// before/after snapshots are JSON strings; show the money fields as text rather than raw JSON.
const SnapshotView = ({ json }) => {
    if (!json) return <span>-</span>;
    let obj;
    try { obj = JSON.parse(json); } catch (e) { return <code style={{ whiteSpace: 'pre-wrap' }}>{String(json)}</code>; }
    if (!obj || typeof obj !== 'object') return <span>{String(obj)}</span>;
    const labels = {
        submitted: 'มียอดส่งแล้ว', locked: 'ล็อก', cashDenominations: 'เงินสด', cashTotal: 'รวมเงินสด', expenses: 'ค่าใช้จ่าย',
        expensesTotal: 'รวมค่าใช้จ่าย', bankTransferAmount: 'ยอดโอนรวม', bankTransfers: 'ยอดโอนรายครั้ง',
    };
    const fmt = (k, v) => {
        if (k === 'locked' || k === 'submitted') return v ? 'ใช่' : 'ไม่';
        if (k === 'cashDenominations' && Array.isArray(v)) {
            return v.length ? v.map((d) => `${num(d.denomination)}×${denomCount(d)}`).join(', ') : '-';
        }
        if (k === 'expenses' && Array.isArray(v)) {
            return v.length ? v.map((e) => `${expenseLabel(e) || '-'} ${num(e.amount)}`).join(', ') : '-';
        }
        if (k === 'bankTransfers' && Array.isArray(v)) {
            return v.length ? v.map((t, i) => `ครั้งที่ ${i + 1}: ${num(t)}`).join(', ') : '-';
        }
        if (v === null || v === undefined) return '-';
        if (typeof v === 'number') return num(v);
        if (typeof v === 'object') return JSON.stringify(v);
        return String(v);
    };
    return (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
            {Object.entries(obj).map(([k, v]) => (
                <li key={k}><strong>{labels[k] || k}:</strong> {fmt(k, v)}</li>
            ))}
        </ul>
    );
};

// mode='agent' = หน้าใบเอเย่นต์ (เช่น AGC-TOKYO หน้าโรง ไม่มีคนขับ/IMD): บัญชีเป็นผู้ขายส่วนหนึ่ง จึงกรอก "เงินที่ส่ง" เองได้
// (Vector 2026-10-11) — ฟอร์มเดียวกับหน้าส่งเงินของ IMD, แหล่ง ACCOUNTING, แก้ซ้ำมี audit, เทียบกับยอดที่ต้องส่งตามระบบ
const ImdMoneySection = ({ reportCode, mode = 'imd', onSaved }) => {
    const agent = mode === 'agent';
    const [refs, setRefs] = useState({ denoms: [], expenses: [] });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', error: false });
    const [busy, setBusy] = useState(false);

    const [unlockOpen, setUnlockOpen] = useState(false);
    const [unlockReason, setUnlockReason] = useState('');

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);

    const [expanded, setExpanded] = useState({});

    const showError = (text) => setMessage({ text, error: true });
    const showOk = (text) => setMessage({ text, error: false });

    const load = useCallback(async () => {
        if (!reportCode) return;
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/rounds/${encodeURIComponent(reportCode)}`, {
                headers: { Accept: 'application/json', Authorization: `Bearer ${authToken()}` },
            });
            if (!res.ok) {
                showError('โหลดเงินที่ IMD ส่งมาไม่สำเร็จ: ' + (await readError(res)));
                return;
            }
            setData(await res.json());
        } catch (e) {
            showError('โหลดเงินที่ IMD ส่งมาไม่สำเร็จ: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, [reportCode]);

    useEffect(() => { load(); }, [load]);

    // ชนิดแบงค์/เหรียญ + หมวดรายจ่าย (บัญชีดูแลที่เว็บ เดียวกับที่ IMD ใช้) — โหลดเฉพาะโหมดกรอกเอง
    useEffect(() => {
        if (!agent) return undefined;
        let cancelled = false;
        const get = async (category) => {
            try {
                const res = await fetch(`${API_BASE_URL}/saledata/api/module/payment-codes?category=${category}`, {
                    headers: { Accept: 'application/json', Authorization: `Bearer ${authToken()}` },
                });
                return res.ok ? await res.json() : [];
            } catch (e) { return []; }
        };
        Promise.all([get('CASH_DENOMINATION'), get('EXPENSE')]).then(([c, x]) => {
            if (cancelled) return;
            const denoms = (Array.isArray(c) ? c : []).map((r) => Number(r.paymentValue)).filter((v) => Number.isFinite(v) && v > 0);
            setRefs({
                denoms: Array.from(new Set(denoms)).sort((a, b) => b - a),
                expenses: (Array.isArray(x) ? x : []).filter((r) => r.code).map((r) => ({ code: r.code, name: r.name || r.code })),
            });
        });
        return () => { cancelled = true; };
    }, [agent]);

    const send = async (method, path, body, okText) => {
        setBusy(true);
        setMessage({ text: '', error: false });
        try {
            const res = await fetch(`${BASE}/rounds/${encodeURIComponent(reportCode)}/${path}`, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                showError('บันทึกไม่สำเร็จ: ' + (await readError(res)));
                return false;
            }
            setData(await res.json());
            showOk(okText);
            if (onSaved) onSaved();
            return true;
        } catch (e) {
            showError('บันทึกไม่สำเร็จ: ' + e.message);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const doUnlock = async () => {
        const reason = unlockReason.trim();
        if (!reason) { showError('กรุณาระบุเหตุผลที่ปลดล็อก'); return; }
        if (await send('POST', 'unlock', { reason }, 'ปลดล็อกแล้ว คนขับส่งใหม่ได้')) {
            setUnlockOpen(false);
            setUnlockReason('');
        }
    };

    const startEdit = () => {
        const existingCash = (data?.cashDenominations || []).map((d) => ({ denomination: d.denomination, count: String(denomCount(d)) }));
        // โหมดกรอกเอง: ให้ครบทุกชนิดแบงค์/เหรียญเหมือนหน้า IMD (ชนิดที่ยังไม่เคยกรอก = ว่าง)
        const cashRows = agent
            ? [...refs.denoms.map((v) => existingCash.find((c) => Number(c.denomination) === v) || { denomination: v, count: '' }),
                ...existingCash.filter((c) => !refs.denoms.includes(Number(c.denomination)))]
            : existingCash;
        setDraft({
            cash: cashRows,
            expenses: (data?.expenses || []).map((e) => ({
                label: expenseLabel(e), amount: e.amount == null ? '' : String(e.amount), note: e.note ?? null,
            })),
            transfers: transfersOf(data).map(String),
            reason: '',
        });
        setEditing(true);
        setUnlockOpen(false);
        setMessage({ text: '', error: false });
    };

    const cancelEdit = () => { setEditing(false); setDraft(null); setMessage({ text: '', error: false }); };

    const draftCash = () => (draft?.cash || []).map((d) => ({ denomination: Number(d.denomination) || 0, quantity: d.count === '' ? 0 : Number(d.count) }));
    const draftExpenses = () => (draft?.expenses || []).map((e) => ({ label: e.label, amount: e.amount === '' ? 0 : Number(e.amount), note: e.note }));

    const draftTransfers = () => (draft?.transfers || []).map((t) => (t === '' ? NaN : Number(t)));

    const saveEdit = async () => {
        const reason = (draft?.reason || '').trim();
        if (!reason) { showError(data?.submitted ? 'กรุณาระบุเหตุผลที่แก้ยอด' : 'กรุณาระบุหมายเหตุ/เหตุผลที่กรอก'); return; }
        const cash = draftCash();
        const exps = draftExpenses();
        if (cash.some((d) => !Number.isFinite(d.quantity) || d.quantity < 0) || exps.some((e) => !Number.isFinite(e.amount))) {
            showError('จำนวน/ยอดต้องเป็นตัวเลข');
            return;
        }
        const transfers = draftTransfers();
        if (transfers.some((t) => !Number.isFinite(t) || t <= 0)) {
            showError('ยอดโอนแต่ละรายการต้องเป็นตัวเลขมากกว่า 0 (ไม่ใช้แถวไหนให้กดลบ)');
            return;
        }
        const body = {
            // DTO field names (CashDenominationEntry / ExpenseEntry) — see note at denomCount above.
            cashDenominations: cash.map((d) => ({ denomination: d.denomination, quantity: d.quantity })),
            expenses: exps.map((e) => ({ category: e.label, amount: e.amount, note: e.note ?? null })),
            bankTransfers: transfers,
            reason,
        };
        if (await send('PUT', 'amounts', body, data?.submitted ? 'แก้ยอดแล้ว' : 'บันทึกเงินที่ส่งแล้ว')) {
            setEditing(false);
            setDraft(null);
        }
    };

    const setCash = (i, v) => setDraft((d) => ({ ...d, cash: d.cash.map((r, j) => (j === i ? { ...r, count: v } : r)) }));
    const setExp = (i, k, v) => setDraft((d) => ({ ...d, expenses: d.expenses.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
    const addExp = () => setDraft((d) => ({ ...d, expenses: [...d.expenses, { label: agent ? (refs.expenses[0]?.code || 'OTHER') : '', amount: '', note: null }] }));
    const setTransfer = (i, v) => setDraft((d) => ({ ...d, transfers: d.transfers.map((t, j) => (j === i ? v : t)) }));
    const addTransfer = () => setDraft((d) => ({ ...d, transfers: [...d.transfers, ''] }));
    const removeTransfer = (i) => setDraft((d) => ({ ...d, transfers: d.transfers.filter((_, j) => j !== i) }));
    const removeExp = (i) => setDraft((d) => ({ ...d, expenses: d.expenses.filter((_, j) => j !== i) }));

    if (!reportCode) return null;

    const status = statusOf(data);
    const inputStyle = { width: 90, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' };

    const cashRows = editing ? draftCash() : (data?.cashDenominations || []).map((d) => ({ denomination: d.denomination, quantity: denomCount(d) }));
    const expRows = editing ? draftExpenses() : (data?.expenses || []).map((e) => ({ label: expenseLabel(e), amount: e.amount }));
    const cashTotal = editing ? cashTotalOf(cashRows) : (data?.cashTotal ?? cashTotalOf(cashRows));
    const expensesTotal = editing ? expensesTotalOf(expRows) : (data?.expensesTotal ?? expensesTotalOf(expRows));
    const transferRows = editing ? draft.transfers : transfersOf(data);
    const bank = transferRows.reduce((sum, t) => sum + (Number(t) || 0), 0);
    const grandTotal = (Number(cashTotal) || 0) + (Number(expensesTotal) || 0) + bank;

    const audits = data?.audits || [];

    return (
        <div className="btc-section" data-testid="imd-money-section">
            <h3>{agent ? 'เงินที่ส่ง (บัญชีกรอก)' : 'เงินที่ IMD ส่งมา'}</h3>
            {loading && !data && <p>กำลังโหลด...</p>}
            {data && (
                <>
                    <p>
                        <strong>สถานะ:</strong>{' '}
                        <span style={{ color: status.color, fontWeight: 'bold' }}>{status.text}</span>
                        {data.submittedAt && <> · ส่งเมื่อ {formatIct(data.submittedAt)}</>}
                        {data.unlockedAt && <> · ปลดล็อกเมื่อ {formatIct(data.unlockedAt)}</>}
                    </p>
                    {data.mismatchReason && <p><strong>เหตุผลยอดไม่ตรง (คนขับ):</strong> {data.mismatchReason}</p>}

                    {(data.submitted || editing) && (
                        <table className="btc-table">
                            <thead>
                                <tr><th>รายการ</th><th>จำนวน</th><th>ยอด (บาท)</th>{editing && <th></th>}</tr>
                            </thead>
                            <tbody>
                                {cashRows.map((d, i) => (
                                    <tr key={`c-${i}`}>
                                        <td style={{ textAlign: 'left' }}>ธนบัตร/เหรียญ {num(d.denomination)}</td>
                                        <td>
                                            {editing ? (
                                                <input type="number" min="0" aria-label={`จำนวน ${d.denomination}`} value={draft.cash[i].count}
                                                    onChange={(e) => setCash(i, e.target.value)} style={inputStyle} />
                                            ) : num(d.quantity)}
                                        </td>
                                        <td>{num((Number(d.denomination) || 0) * (Number(d.quantity) || 0))}</td>
                                        {editing && <td></td>}
                                    </tr>
                                ))}
                                <tr style={{ backgroundColor: '#f1f3f5', fontWeight: 'bold' }}>
                                    <td colSpan={2} style={{ textAlign: 'right' }}>รวมเงินสด:</td>
                                    <td>{num(cashTotal)}</td>{editing && <td></td>}
                                </tr>
                                {expRows.map((e, i) => (
                                    <tr key={`e-${i}`}>
                                        <td style={{ textAlign: 'left' }}>
                                            {editing ? (
                                                agent && refs.expenses.length > 0 ? (
                                                    <select aria-label={`ค่าใช้จ่าย ${i + 1}`} value={draft.expenses[i].label}
                                                        onChange={(ev) => setExp(i, 'label', ev.target.value)} style={{ ...inputStyle, width: 160 }}>
                                                        {!refs.expenses.some((r) => r.code === draft.expenses[i].label) && <option value={draft.expenses[i].label}>{draft.expenses[i].label || '-'}</option>}
                                                        {refs.expenses.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                                                    </select>
                                                ) : (
                                                    <input type="text" aria-label={`ค่าใช้จ่าย ${i + 1}`} value={draft.expenses[i].label}
                                                        onChange={(ev) => setExp(i, 'label', ev.target.value)} style={{ ...inputStyle, width: 160 }} />
                                                )
                                            ) : <>ค่าใช้จ่าย: {e.label || '-'}</>}
                                        </td>
                                        <td></td>
                                        <td>
                                            {editing ? (
                                                <input type="number" aria-label={`ยอดค่าใช้จ่าย ${i + 1}`} value={draft.expenses[i].amount}
                                                    onChange={(ev) => setExp(i, 'amount', ev.target.value)} style={inputStyle} />
                                            ) : num(e.amount)}
                                        </td>
                                        {editing && (
                                            <td><button type="button" onClick={() => removeExp(i)}>ลบ</button></td>
                                        )}
                                    </tr>
                                ))}
                                {editing && (
                                    <tr><td colSpan={4} style={{ textAlign: 'left' }}>
                                        <button type="button" onClick={addExp}>+ เพิ่มค่าใช้จ่าย</button>
                                    </td></tr>
                                )}
                                <tr style={{ backgroundColor: '#f1f3f5', fontWeight: 'bold' }}>
                                    <td colSpan={2} style={{ textAlign: 'right' }}>รวมค่าใช้จ่าย:</td>
                                    <td>{num(expensesTotal)}</td>{editing && <td></td>}
                                </tr>
                                {transferRows.map((t, i) => (
                                    <tr key={`t-${i}`}>
                                        <td colSpan={2} style={{ textAlign: 'left' }}>ยอดโอน ครั้งที่ {i + 1}</td>
                                        <td>
                                            {editing ? (
                                                <input type="number" min="0" aria-label={`ยอดโอน ครั้งที่ ${i + 1}`} value={draft.transfers[i]}
                                                    onChange={(e) => setTransfer(i, e.target.value)} style={inputStyle} />
                                            ) : num(t)}
                                        </td>
                                        {editing && (
                                            <td><button type="button" onClick={() => removeTransfer(i)}>ลบ</button></td>
                                        )}
                                    </tr>
                                ))}
                                {editing && (
                                    <tr><td colSpan={4} style={{ textAlign: 'left' }}>
                                        <button type="button" onClick={addTransfer}>+ เพิ่มรายการโอน</button>
                                    </td></tr>
                                )}
                                <tr style={{ backgroundColor: '#f1f3f5', fontWeight: 'bold' }}>
                                    <td colSpan={2} style={{ textAlign: 'right' }}>รวมยอดโอน:</td>
                                    <td data-testid="imd-transfer-total">{num(bank)}</td>{editing && <td></td>}
                                </tr>
                                <tr style={{ backgroundColor: '#b1becc', fontWeight: 'bold' }}>
                                    <td colSpan={2} style={{ textAlign: 'right' }}>รวมทั้งสิ้น (เงินสด + ค่าใช้จ่าย + โอน):</td>
                                    <td data-testid="imd-grand-total">{num(grandTotal)}</td>{editing && <td></td>}
                                </tr>
                            </tbody>
                        </table>
                    )}

                    {agent && (data.submitted || editing) && (
                        <div data-testid="agent-money-compare" style={{ margin: '8px 0', padding: '8px 12px', background: '#f8f9fa', borderRadius: 6 }}>
                            {data.expected && data.expected.cashDue != null ? (() => {
                                const due = Number(data.expected.cashDue);
                                const diff = grandTotal - due;
                                const tone = diffTone(diff);
                                return (
                                    <>
                                        <div>ยอดที่ต้องส่งตามระบบ: <strong data-testid="agent-expected-due">{num(due)}</strong> บาท
                                            <span style={{ color: '#6c757d', fontSize: 12 }}> (ขาย {num(data.expected.salesTotal)} + เงินประกันกระสอบ {num(data.expected.bagsaleTotal)} − ค้างจ่าย {num(data.expected.creditTotal)} + เก็บค้างเก่า {num(data.expected.collectedTotal)})</span>
                                        </div>
                                        <div>ยอดที่กรอก (เงินสด + ค่าใช้จ่าย + โอน): <strong>{num(grandTotal)}</strong> บาท</div>
                                        <div>ผลต่าง: <strong data-testid="agent-diff" style={{ color: tone.color }}>{diff > 0 ? '+' : ''}{num(diff)} ({tone.text})</strong></div>
                                    </>
                                );
                            })() : <div style={{ color: '#ef6c00' }}>คำนวณยอดที่ต้องส่งตามระบบไม่ได้ตอนนี้ (ไม่มียอดขายของรอบหรือต่อ saledata ไม่ได้) — ยังเทียบผลต่างไม่ได้</div>}
                        </div>
                    )}

                    {agent && !data.submitted && !editing && (
                        <div style={{ margin: '6px 0 12px' }}>
                            <button className="btc-btn btn-sm" style={{ backgroundColor: '#2e7d32' }} onClick={startEdit} disabled={busy || refs.denoms.length === 0} data-testid="agent-money-start">
                                กรอกเงินที่ส่ง
                            </button>
                            {refs.denoms.length === 0 && <span style={{ color: '#6c757d', marginLeft: 8 }}>กำลังโหลดชนิดแบงค์...</span>}
                        </div>
                    )}

                    {(data.submitted || editing) && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0 12px' }}>
                            {!editing && data.locked && !unlockOpen && data.source !== 'ACCOUNTING' && (
                                <button className="btc-btn btn-sm" style={{ backgroundColor: '#ef6c00' }} onClick={() => { setUnlockOpen(true); setMessage({ text: '', error: false }); }} disabled={busy}>
                                    ปลดล็อกให้คนขับส่งใหม่
                                </button>
                            )}
                            {unlockOpen && !editing && (
                                <>
                                    <input type="text" placeholder="เหตุผลที่ปลดล็อก (ต้องกรอก)" aria-label="เหตุผลที่ปลดล็อก" value={unlockReason}
                                        onChange={(e) => setUnlockReason(e.target.value)} style={{ ...inputStyle, width: 260 }} />
                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#ef6c00' }} onClick={doUnlock} disabled={busy}>
                                        {busy ? 'กำลังบันทึก...' : 'ยืนยันปลดล็อก'}
                                    </button>
                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#6c757d' }} onClick={() => { setUnlockOpen(false); setUnlockReason(''); }} disabled={busy}>
                                        ยกเลิก
                                    </button>
                                </>
                            )}
                            {!editing && !unlockOpen && (
                                <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={startEdit} disabled={busy}>
                                    แก้ยอด
                                </button>
                            )}
                            {editing && (
                                <>
                                    <input type="text" placeholder={data.submitted ? 'เหตุผลที่แก้ยอด (ต้องกรอก)' : 'หมายเหตุ/เหตุผลที่กรอก (ต้องกรอก)'} aria-label="เหตุผลที่แก้ยอด" value={draft.reason}
                                        onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))} style={{ ...inputStyle, width: 260 }} />
                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#343a40' }} onClick={saveEdit} disabled={busy}>
                                        {busy ? 'กำลังบันทึก...' : (data.submitted ? 'บันทึกยอด' : 'บันทึกเงินที่ส่ง')}
                                    </button>
                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#6c757d' }} onClick={cancelEdit} disabled={busy}>
                                        ยกเลิก
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
            {message.text && (
                <p role={message.error ? 'alert' : 'status'} style={{ color: message.error ? '#c62828' : '#2e7d32', margin: '4px 0 12px' }}>
                    {message.text}
                </p>
            )}

            {data && (
                <>
                    <h4 style={{ marginTop: 16 }}>ประวัติการแก้/ปลดล็อก</h4>
                    {audits.length === 0 ? <p>ไม่มีประวัติ</p> : (
                        <table className="btc-table">
                            <thead>
                                <tr><th>เวลา</th><th>ผู้ทำ</th><th>การกระทำ</th><th>เหตุผล</th><th></th></tr>
                            </thead>
                            <tbody>
                                {audits.map((a, i) => {
                                    const key = a.id ?? i;
                                    return (
                                        <React.Fragment key={key}>
                                            <tr>
                                                <td>{formatIct(a.createdAt)}</td>
                                                <td>{a.actorName || a.actorUid || '-'}</td>
                                                <td>{ACTION_LABEL[a.action] || a.action}</td>
                                                <td style={{ textAlign: 'left' }}>{a.reason || '-'}</td>
                                                <td>
                                                    <button type="button" onClick={() => setExpanded((m) => ({ ...m, [key]: !m[key] }))}>
                                                        {expanded[key] ? 'ซ่อน' : 'ก่อน/หลัง'}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expanded[key] && (
                                                <tr>
                                                    <td colSpan={5} style={{ textAlign: 'left' }}>
                                                        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                                            <div><strong>ก่อน</strong><SnapshotView json={a.beforeJson} /></div>
                                                            <div><strong>หลัง</strong><SnapshotView json={a.afterJson} /></div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </div>
    );
};

export default ImdMoneySection;
